import type { ReactNode } from 'react';
import { CaretRightIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { shortPersonName } from './person_name.js';
import { PersonAvatar, type PersonItem } from './person_row.js';
import { useTravelerPickerStage } from './traveler_picker_stage.js';

/**
 * "Aplicar política de": o rótulo e a pessoa, numa pílula que abre a lista para
 * trocar. Uma só para todos os campos QUEM (aéreo, ônibus, hotel); quem decide
 * se ela aparece (2+ viajantes) é o chamador.
 */
export function TravelerPolicyRow({
  subject,
  requesterId,
  label,
  youLabel,
  locale,
  disabled = false,
  onChoose,
}: {
  readonly subject: PersonItem;
  readonly requesterId: string | undefined;
  /** "Aplicar política de" — também prefixa o nome acessível da pílula. */
  readonly label: string;
  /** Como o solicitante aparece quando a política é dele ("Você"). */
  readonly youLabel: string;
  readonly locale?: string;
  readonly disabled?: boolean;
  readonly onChoose: () => void;
}): ReactNode {
  const name = subject.id === requesterId ? youLabel : shortPersonName(subject.name, locale);
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-caption text-muted-foreground">{label}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1.5 rounded-full pr-1.5 pl-1"
        aria-label={`${label} ${name}`}
        disabled={disabled}
        onClick={onChoose}
      >
        <PersonAvatar
          name={subject.name}
          photoUrl={subject.photoUrl}
          size="xs"
          {...(locale ? { locale } : {})}
        />
        <span className="text-ui truncate font-medium">{name}</span>
        <CaretRightIcon className="size-3.5 text-muted-foreground" aria-hidden="true" />
      </Button>
    </div>
  );
}

/** O pé do seletor: a política (quando há), a contagem anunciada e o fechar.
 *  No popover o «Pronto» é pequeno, no canto da contagem; na folha é o botão
 *  grande de largura total das outras folhas do kit, com a contagem acima. */
export function TravelerPickerFooter({
  policy,
  summary,
  doneLabel,
  onDone,
}: {
  /** A `TravelerPolicyRow`, ou nada com um viajante só. */
  readonly policy?: ReactNode;
  /** "2 viajantes na busca" — anunciado a cada mudança. */
  readonly summary: string;
  readonly doneLabel: string;
  readonly onDone: () => void;
}): ReactNode {
  const announced = (
    <p className="text-caption text-muted-foreground" aria-live="polite">
      {summary}
    </p>
  );
  if (useTravelerPickerStage() === 'sheet') {
    return (
      <>
        {policy}
        {announced}
        <Button type="button" size="xl" className="w-full rounded-full" onClick={onDone}>
          {doneLabel}
        </Button>
      </>
    );
  }
  return (
    <>
      {policy}
      <div className="flex items-center justify-between gap-2">
        {announced}
        <Button type="button" size="sm" onClick={onDone}>
          {doneLabel}
        </Button>
      </div>
    </>
  );
}
